import { Badge, Button } from '@paguz/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRightIcon, CalendarClockIcon, SlidersHorizontalIcon, ZapIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { api, queryKeys } from '../../api/endpoints.ts';
import { PageHeader, Panel } from '../../components/page.tsx';
import { formatLongDate } from '../../lib/format.ts';
import { can } from '../../lib/rbac.ts';
import { useSession } from '../../lib/session.ts';
import { FAULT_MODES } from '../demo/fault-modes.ts';
import { Dashboard } from './dashboard.tsx';

/** Inicio: estado operativo rápido y accesos a las escenas del demo (el dashboard completo es T-CON-08). */
export function HomePage() {
  const session = useSession();
  const roles = session?.user.roles ?? [];
  const canTx = can(roles, 'transactions:read');
  const canDemo = can(roles, 'demo:operate');
  const clock = useQuery({ queryKey: queryKeys.clock, queryFn: api.getClock });
  const fault = useQuery({
    queryKey: queryKeys.faultMode,
    queryFn: api.getFaultMode,
    enabled: canDemo,
  });

  const firstName = session?.user.fullName.split(' ')[0] ?? '';

  return (
    <>
      <PageHeader
        eyebrow={clock.data ? formatLongDate(clock.data.now) : undefined}
        title={`Hola, ${firstName}`}
        description="Esto es lo que necesita atención en la operación de crédito rotativo con Bre-B."
      />

      <Dashboard />

      <div className="grid gap-6 lg:grid-cols-2">
        {canDemo && (
          <Panel
            title="Estado del demo"
            actions={
              <Button asChild size="sm" variant="outline">
                <Link to="/demo">
                  Controles de demo <ArrowRightIcon />
                </Link>
              </Button>
            }
          >
            <ul className="flex flex-col gap-3 text-sm">
              <li className="flex items-center gap-3">
                <CalendarClockIcon className="size-5 text-primary" />
                Reloj:{' '}
                {clock.data?.mode === 'SIMULATED' ? (
                  <Badge variant="warning">Simulado +{clock.data.offsetDays} días</Badge>
                ) : (
                  <Badge variant="success">Tiempo real</Badge>
                )}
              </li>
              <li className="flex items-center gap-3">
                <ZapIcon className="size-5 text-primary" />
                Fallas Bre-B:{' '}
                {fault.data && fault.data.mode !== 'NORMAL' ? (
                  <Badge variant="warning">
                    {FAULT_MODES[fault.data.mode].label} · próximas {fault.data.remaining}
                  </Badge>
                ) : (
                  <Badge variant="success">Normal</Badge>
                )}
              </li>
            </ul>
          </Panel>
        )}
        <Panel title="Atajos">
          <div className="grid gap-2 sm:grid-cols-2">
            {can(roles, 'applications:read') && (
              <Shortcut
                to="/expedientes"
                title="Expedientes"
                text="Decidir solicitudes con la recomendación del motor"
              />
            )}
            {can(roles, 'agents:read') && (
              <Shortcut to="/agentes" title="Agentes" text="Sesiones, trazas y presupuesto LLM" />
            )}
            {canTx && (
              <Shortcut
                to="/transacciones"
                title="Transacciones"
                text="Timeline de la saga y acciones de operador"
              />
            )}
            {can(roles, 'customers:read') && (
              <Shortcut
                to="/clientes"
                title="Clientes y líneas"
                text="Fichas, cupos, cuotas y extractos"
              />
            )}
            {can(roles, 'config:read') && (
              <Shortcut
                to="/configuracion"
                title="Configuración"
                text="Políticas de decisión, reversión y mora"
                icon={<SlidersHorizontalIcon />}
              />
            )}
            {can(roles, 'audit:read') && (
              <Shortcut to="/auditoria" title="Auditoría" text="Búsqueda y cadena de hashes" />
            )}
          </div>
        </Panel>
      </div>
    </>
  );
}

function Shortcut({
  to,
  title,
  text,
  icon,
}: {
  to:
    '/transacciones' | '/clientes' | '/configuracion' | '/auditoria' | '/expedientes' | '/agentes';
  title: string;
  text: string;
  icon?: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="flex items-start gap-3 rounded-xl border border-border p-4 transition-colors hover:border-highlight/40 hover:bg-highlight-soft/40"
    >
      {icon && <span className="mt-0.5 text-primary [&_svg]:size-5">{icon}</span>}
      <span>
        <span className="block font-semibold">{title}</span>
        <span className="text-sm text-muted-foreground">{text}</span>
      </span>
    </Link>
  );
}
